import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import Command from '@ckeditor/ckeditor5-core/src/command';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import { toWidget, toWidgetEditable } from '@ckeditor/ckeditor5-widget/src/utils';

class InsertCollapsibleBlockCommand extends Command {
	execute() {
		const model = this.editor.model;

		// 1단계: 블록 삽입
		let collapsibleTitle;
		model.change(writer => {
			const collapsibleBlock = writer.createElement('collapsibleBlock');
			collapsibleTitle = writer.createElement('collapsibleTitle');
			const collapsibleContent = writer.createElement('collapsibleContent');
			const paragraph = writer.createElement('paragraph');

			writer.append(collapsibleTitle, collapsibleBlock);
			writer.append(collapsibleContent, collapsibleBlock);
			writer.append(paragraph, collapsibleContent);

			model.insertObject(collapsibleBlock, null, null, { setSelection: 'after' });
		});

		// 2단계: 삽입 완료 후 커서를 제목 영역으로 이동
		model.change(writer => {
			writer.setSelection(collapsibleTitle, 'in');
		});
	}

	refresh() {
		const model = this.editor.model;
		const selection = model.document.selection;
		const allowedIn = model.schema.findAllowedParent(
			selection.getFirstPosition(),
			'collapsibleBlock'
		);
		this.isEnabled = allowedIn !== null;
	}
}

class CollapsibleBlock extends Plugin {
	static get requires() {
		return [Widget];
	}

	init() {
		this._defineSchema();
		this._defineConverters();

		this.editor.commands.add(
			'insertCollapsibleBlock',
			new InsertCollapsibleBlockCommand(this.editor)
		);

		// 제목 영역에서 Tab: 본문 영역으로 커서 이동
		this.editor.keystrokes.set('tab', (evt, cancel) => {
			const model = this.editor.model;
			const position = model.document.selection.getFirstPosition();

			if (!position.findAncestor('collapsibleTitle')) return;

			let node = position.parent;
			let collapsibleBlock = null;
			while (node && node.name !== '$root') {
				if (node.name === 'collapsibleBlock') { collapsibleBlock = node; break; }
				node = node.parent;
			}
			if (!collapsibleBlock) return;

			const content = collapsibleBlock.getChild(1); // collapsibleContent
			if (!content) return;

			cancel();
			evt.preventDefault();

			model.change(writer => {
				writer.setSelection(writer.createPositionAt(content, 0));
			});
		}, { priority: 'highest' });

		// 내용 영역에서 빈 줄에 Enter: 블록 밖으로 탈출
		this.editor.keystrokes.set('enter', (evt, cancel) => {
			const model = this.editor.model;
			const selection = model.document.selection;
			const position = selection.getFirstPosition();

			const paragraph = position.findAncestor('paragraph');
			if (!paragraph) return;
			if (!paragraph.isEmpty) return;
			if (paragraph.parent && paragraph.parent.name !== 'collapsibleContent') return;

			let node = paragraph.parent;
			let collapsibleBlock = null;
			while (node && node.name !== '$root') {
				if (node.name === 'collapsibleBlock') {
					collapsibleBlock = node;
					break;
				}
				node = node.parent;
			}
			if (!collapsibleBlock) return;

			const blockRef = collapsibleBlock;
			const paragraphRef = paragraph;

			cancel();
			evt.preventDefault();

			model.change(writer => {
				// 노드가 여전히 문서에 붙어 있는지 확인 후 실행
				if (!paragraphRef.parent) return;
				if (!blockRef.parent) return;
				writer.remove(paragraphRef);
				const newParagraph = writer.createElement('paragraph');
				writer.insert(newParagraph, writer.createPositionAfter(blockRef));
				writer.setSelection(newParagraph, 'in');
			});
		}, { priority: 'highest' });

		this.editor.ui.componentFactory.add('collapsibleBlock', locale => {
			const button = new ButtonView(locale);
			button.set({
				label: '접이식 블록 (Alt+D)',
				icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M5 7l5 5 5-5H5z"/><rect x="3" y="4" width="14" height="1.5" rx="0.75"/><rect x="3" y="15" width="10" height="1.5" rx="0.75" opacity="0.4"/></svg>',
				tooltip: true,
				withText: false,
				class: 'ck-collapsible-button',
			});

			const command = this.editor.commands.get('insertCollapsibleBlock');
			button.bind('isEnabled').to(command);

			button.on('execute', () => {
				this.editor.execute('insertCollapsibleBlock');
				this.editor.editing.view.focus();
			});

			return button;
		});
	}

	_defineSchema() {
		const schema = this.editor.model.schema;

		schema.register('collapsibleBlock', {
			isObject: true,
			allowWhere: '$block',
		});

		schema.register('collapsibleTitle', {
			isLimit: true,
			allowIn: 'collapsibleBlock',
			allowContentOf: '$block',
		});

		schema.register('collapsibleContent', {
			isLimit: true,
			allowIn: 'collapsibleBlock',
			allowContentOf: '$root',
		});
	}

	_defineConverters() {
		const conversion = this.editor.conversion;

		// collapsibleBlock: <details>
		conversion.for('upcast').elementToElement({
			model: 'collapsibleBlock',
			view: 'details',
		});
		conversion.for('dataDowncast').elementToElement({
			model: 'collapsibleBlock',
			view: (modelElement, { writer }) =>
				writer.createContainerElement('details', { class: 'collapsible-block' }),
		});
		conversion.for('editingDowncast').elementToElement({
			model: 'collapsibleBlock',
			view: (modelElement, { writer }) => {
				const div = writer.createContainerElement('div', {
					class: 'collapsible-block-widget',
				});
				return toWidget(div, writer, { label: '접이식 블록' });
			},
		});

		// collapsibleTitle: <summary>
		conversion.for('upcast').elementToElement({
			model: 'collapsibleTitle',
			view: 'summary',
		});
		conversion.for('dataDowncast').elementToElement({
			model: 'collapsibleTitle',
			view: (modelElement, { writer }) =>
				writer.createContainerElement('summary'),
		});
		conversion.for('editingDowncast').elementToElement({
			model: 'collapsibleTitle',
			view: (modelElement, { writer }) => {
				const div = writer.createEditableElement('div', {
					class: 'collapsible-block-title',
					'data-placeholder': '제목을 입력하세요...',
				});
				return toWidgetEditable(div, writer);
			},
		});

		// collapsibleContent: <div class="collapsible-content">
		conversion.for('upcast').elementToElement({
			model: 'collapsibleContent',
			view: { name: 'div', classes: 'collapsible-content' },
		});
		conversion.for('dataDowncast').elementToElement({
			model: 'collapsibleContent',
			view: (modelElement, { writer }) =>
				writer.createContainerElement('div', { class: 'collapsible-content' }),
		});
		conversion.for('editingDowncast').elementToElement({
			model: 'collapsibleContent',
			view: (modelElement, { writer }) => {
				const div = writer.createEditableElement('div', {
					class: 'collapsible-block-content',
				});
				return toWidgetEditable(div, writer);
			},
		});
	}
}

export default CollapsibleBlock;
